import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { io } from "socket.io-client";

export default function StudentPage() {
  const { token, user, logout } = useAuth();
  const navigate = useNavigate();
  const [sessionCode, setSessionCode] = useState("");
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [waiting, setWaiting] = useState(false);
  const socketRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    const socket = io({ auth: { token } });
    socketRef.current = socket;
    socket.emit("auth", token);
    socket.on("auth_error", () => { logout(); navigate("/login"); });
    socket.on("message", msg => setMessages(prev => [...prev, msg]));
    socket.on("history", msgs => setMessages(msgs));
    socket.on("session_status", ({ status }) => {
      if (status === "active") setWaiting(false);
      setCurrentSession(prev => prev ? { ...prev, status } : prev);
    });
    socket.on("paused_block", (text) => { alert(text || "先生がチャットを一時停止しています。"); });
    return () => socket.disconnect();
  }, [token]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function joinByCode(e) {
    e.preventDefault();
    setError("");
    if (!sessionCode.trim()) return;
    try {
      const r = await fetch("/api/sessions/code/" + sessionCode.trim().toUpperCase(), {
        headers: { Authorization: "Bearer " + token }
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setCurrentSession(d);
      socketRef.current?.emit("join_session", d.id);
      if (d.status === "waiting") setWaiting(true);
      else setWaiting(false);
    } catch(err) {
      setError(err.message);
    }
  }

  function sendMessage(e) {
    e.preventDefault();
    if (!input.trim() || !currentSession) return;
    socketRef.current?.emit("message", { sessionId: currentSession.id, content: input.trim() });
    setInput("");
  }

  const nickname = user?.nickname || "生徒";

  function getAvatar(name, type) {
    if (type === "bot" && name && name.includes("トラブル")) return { bg: "#7f1d1d", icon: "😈" };
    if (type === "bot") return { bg: "#1e3a5f", icon: "🎓" };
    if (type === "teacher") return { bg: "#0f3460", icon: "👩‍🏫" };
    const colors = ["#166534", "#1e3a5f", "#4a1d96", "#7c2d12", "#134e4a"];
    const idx = name ? name.charCodeAt(0) % colors.length : 0;
    return { bg: colors[idx], icon: name ? name[0] : "?" };
  }

  // 日付区切りを表示するためのグループ化
  function getDateLabel(dateStr) {
    const d = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return "今日";
    if (d.toDateString() === yesterday.toDateString()) return "昨日";
    return d.toLocaleDateString("ja-JP", { month: "long", day: "numeric" });
  }

  let lastDate = "";

  return (
    <div style={styles.wrap}>
      {!currentSession ? (
        <div style={styles.codeWrap}>
          <div style={styles.codeCard}>
            <div style={styles.codeLogo}>💬</div>
            <h1 style={styles.codeTitle}>SNSまなびチャット</h1>
            <p style={styles.codeSub}>ようこそ、{nickname}さん！</p>
            <p style={styles.codeGuide}>先生から教えてもらったコードを入力してね</p>
            {error && <div style={styles.codeError}>{error}</div>}
            <form onSubmit={joinByCode} style={styles.codeForm}>
              <input style={styles.codeInput} value={sessionCode}
                onChange={e => setSessionCode(e.target.value.toUpperCase())}
                placeholder="例：A3K7F" maxLength={6} autoFocus />
              <button style={styles.codeBtn}>参加する</button>
            </form>
            <button style={styles.logoutLink} onClick={() => { logout(); navigate("/login"); }}>
              ログアウト
            </button>
          </div>
        </div>
      ) : waiting ? (
        <div style={styles.codeWrap}>
          <div style={styles.codeCard}>
            <div style={{fontSize:48, marginBottom:8}}>⏳</div>
            <h2 style={styles.codeTitle}>もうすぐ始まります</h2>
            <p style={styles.codeSub}>先生がチャットを開始するまで待ってね</p>
            <p style={{color:"var(--text-muted)", fontSize:13, marginTop:8}}>{currentSession.situation}</p>
            <button style={{...styles.logoutLink, marginTop:24}} onClick={() => { setCurrentSession(null); setSessionCode(""); }}>
              コード入力に戻る
            </button>
          </div>
        </div>
      ) : (
        <div style={styles.chatWrap}>
          {/* ヘッダー */}
          <div style={styles.chatHeader}>
            <div style={styles.headerLeft}>
              <div style={styles.headerIcon}>💬</div>
              <div>
                <div style={styles.headerTitle}>{currentSession.situation}</div>
                <div style={styles.headerSub}>{nickname}として参加中</div>
              </div>
            </div>
            <div style={{display:"flex", alignItems:"center", gap:8}}>
              {currentSession.status === "ended" && (
                <div style={styles.endedBadge}>終了</div>
              )}
              <button style={styles.headerLogout} onClick={() => { logout(); navigate("/login"); }}>ログアウト</button>
            </div>
          </div>

          {/* メッセージ一覧 */}
          <div style={styles.messages}>
            {messages.map((msg, i) => {
              const isMe = msg.sender_name === nickname;
              const av = getAvatar(msg.sender_name, msg.sender_type);
              const timeStr = new Date(msg.created_at).toLocaleTimeString("ja-JP", {hour:"2-digit",minute:"2-digit"});
              const dateLabel = getDateLabel(msg.created_at);
              const showDate = dateLabel !== lastDate;
              if (showDate) lastDate = dateLabel;

              return (
                <React.Fragment key={i}>
                  {showDate && (
                    <div style={styles.dateSep}>
                      <span style={styles.dateLabel}>{dateLabel}</span>
                    </div>
                  )}
                  {isMe ? (
                    // 自分のメッセージ（右）
                    <div style={styles.myMsgRow}>
                      <div style={styles.myMsgMeta}>
                        <span style={styles.msgTimeRight}>{timeStr}</span>
                      </div>
                      <div style={styles.myBubble}>{msg.content}</div>
                    </div>
                  ) : (
                    // 他の人のメッセージ（左）
                    <div style={styles.otherMsgRow}>
                      <div style={{...styles.avatar, background: av.bg}}>{av.icon}</div>
                      <div style={styles.otherMsgBody}>
                        <div style={styles.senderName}>
                          {msg.sender_type === "bot" && msg.sender_name.includes("トラブル") ? (
                            <span style={{color:"#b91c1c"}}>{msg.sender_name}</span>
                          ) : msg.sender_type === "bot" ? (
                            <span style={{color:"#1e40af"}}>{msg.sender_name}</span>
                          ) : (
                            <span>{msg.sender_name}</span>
                          )}
                        </div>
                        <div style={styles.otherMsgMeta}>
                          <div style={styles.otherBubble}>{msg.content}</div>
                          <span style={styles.msgTimeLeft}>{timeStr}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
            <div ref={bottomRef} />
          </div>

          {/* 入力エリア */}
          {currentSession.status === "ended" ? (
            <div style={styles.endedBar}>このチャットは終了しました</div>
          ) : currentSession.status === "paused" ? (
            <div style={{...styles.endedBar, background:"#fde68a", color:"#92400e"}}>
              ⏸ 先生がチャットを一時停止しています。再開までお待ちください。
            </div>
          ) : (
            <form onSubmit={sendMessage} style={styles.inputArea}>
              <input style={styles.chatInput} value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="メッセージを入力..." />
              <button style={{...styles.sendBtn, opacity: input.trim() ? 1 : 0.5}} disabled={!input.trim()}>
                送信
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  wrap: { height: "100vh", background: "#1a1a2e", display: "flex", flexDirection: "column" },
  codeWrap: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 },
  codeCard: { background: "var(--bg-mid)", borderRadius: 16, padding: 40, width: "100%", maxWidth: 400, textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" },
  codeLogo: { fontSize: 48, marginBottom: 8 },
  codeTitle: { fontSize: 22, fontWeight: 700, color: "var(--accent)", marginBottom: 4 },
  codeSub: { color: "var(--text)", fontSize: 16, marginBottom: 8 },
  codeGuide: { color: "var(--text-muted)", fontSize: 13, marginBottom: 20 },
  codeError: { background: "rgba(248,113,113,0.15)", border: "1px solid var(--danger)", borderRadius: 8, padding: "8px 14px", color: "var(--danger)", fontSize: 13, marginBottom: 12 },
  codeForm: { display: "flex", flexDirection: "column", gap: 12 },
  codeInput: { padding: "14px", borderRadius: 8, border: "2px solid var(--accent2)", background: "var(--bg-dark)", color: "var(--text)", fontSize: 28, textAlign: "center", letterSpacing: 8, fontWeight: 700 },
  codeBtn: { padding: "14px", borderRadius: 8, background: "var(--accent)", color: "#000", fontWeight: 700, fontSize: 16, cursor: "pointer", border: "none" },
  logoutLink: { marginTop: 16, background: "transparent", border: "none", color: "var(--text-muted)", fontSize: 12, cursor: "pointer", textDecoration: "underline" },
  chatWrap: { display: "flex", flexDirection: "column", height: "100vh", background: "#b2d8d8" },
  chatHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", background: "#00b900", borderBottom: "1px solid rgba(0,0,0,0.1)" },
  headerLeft: { display: "flex", alignItems: "center", gap: 10 },
  headerIcon: { fontSize: 28 },
  headerTitle: { color: "#fff", fontWeight: 700, fontSize: 14, textShadow: "0 1px 2px rgba(0,0,0,0.3)" },
  headerSub: { color: "rgba(255,255,255,0.8)", fontSize: 11 },
  endedBadge: { background: "rgba(248,113,113,0.2)", color: "#f87171", fontSize: 11, padding: "4px 10px", borderRadius: 12, border: "1px solid #f87171" },
  messages: { flex: 1, overflowY: "auto", padding: "12px 16px", display: "flex", flexDirection: "column", gap: 4, background: "#b2d8d8" },
  dateSep: { display: "flex", justifyContent: "center", margin: "8px 0" },
  dateLabel: { background: "rgba(0,0,0,0.15)", color: "#fff", fontSize: 11, padding: "3px 12px", borderRadius: 12 },
  myMsgRow: { display: "flex", justifyContent: "flex-end", alignItems: "flex-end", gap: 6, marginBottom: 2 },
  myMsgMeta: { display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "flex-end" },
  myBubble: { background: "#4ade80", color: "#000", padding: "8px 12px", borderRadius: "16px 16px 4px 16px", fontSize: 14, maxWidth: "70%", wordBreak: "break-word", fontWeight: 500 },
  msgTimeRight: { color: "#1a3a2a", fontSize: 10, marginBottom: 2 },
  otherMsgRow: { display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 2 },
  avatar: { width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: "#fff", flexShrink: 0 },
  otherMsgBody: { display: "flex", flexDirection: "column", gap: 2, maxWidth: "70%" },
  senderName: { fontSize: 11, color: "#1a3a2a", fontWeight:700, paddingLeft: 4 },
  otherMsgMeta: { display: "flex", alignItems: "flex-end", gap: 6 },
  otherBubble: { background: "#fff", color: "#000", padding: "8px 12px", borderRadius: "4px 16px 16px 16px", fontSize: 14, wordBreak: "break-word" },
  msgTimeLeft: { color: "#1a3a2a", fontSize: 10, flexShrink: 0, marginBottom: 2 },
  inputArea: { display: "flex", gap: 8, padding: "10px 12px", background: "#c8e6e6", borderTop: "1px solid rgba(0,0,0,0.1)" },
  chatInput: { flex: 1, padding: "10px 14px", borderRadius: 24, border: "none", background: "#fff", color: "#000", fontSize: 14, outline: "none" },
  sendBtn: { padding: "10px 20px", borderRadius: 24, background: "#4ade80", color: "#000", fontWeight: 700, fontSize: 14, cursor: "pointer", border: "none", flexShrink: 0 },
  endedBar: { padding: "12px 16px", background: "#c8e6e6", color: "#555", fontSize: 13, textAlign: "center" },
  headerLogout: { padding: "4px 10px", borderRadius: 12, background: "rgba(0,0,0,0.2)", border: "none", color: "#fff", fontSize: 11, cursor: "pointer" },
};
